import { Clock, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { formatRemaining, holdClock, type HoldClock, type GroupLock, type PendingHold } from './types';

// A soft-hold lifecycle row (server-truth countdown + Confirm/Release/Re-hold/
// Dismiss). Pure presentation — data + callbacks via props, gated by the group's
// lock. The countdown pieces below are exported because the case-detail
// reservations panel renders the same hold as a wider card: one clock, one copy
// deck, two layouts.
//
// The deadline is ALWAYS the server's (`hold.expiresAt`). The UI has no TTL
// constant, so there are three honest states and no fourth:
//   • no clock   — the server granted none (the BFF's offline fixture path does
//                  exactly this). Show "Hold active", NO countdown, and keep
//                  every action: a missing clock must never read as "expired".
//   • counting   — tick against the ISO instant.
//   • lapsed     — informational, never an error. A soft hold that runs out is
//                  not a failure: acceptance-after-expiry is first-class server
//                  side and the accept path re-acquires, so Confirm STAYS
//                  enabled and Re-hold is only the secondary way back.

/**
 * Deadline chip for a soft inventory hold. Per the guest-experience TDS house
 * rule every countdown also carries its absolute local time — inline as
 * secondary text, and in the tooltip for the truncated case.
 */
export function HoldClockChip({
  clock,
  className,
  'data-testid': testId = 'hold-clock',
}: {
  clock: HoldClock;
  className?: string;
  'data-testid'?: string;
}) {
  const { t } = useTranslation();

  if (!clock.hasClock) {
    return (
      <Badge
        variant="muted"
        className={className}
        data-testid={testId}
        data-clock="none"
        title={t('holds.clock.noneHint', 'The server granted no deadline for this hold')}
      >
        <Clock className="h-3 w-3" aria-hidden />
        {t('holds.clock.active', 'Hold active')}
      </Badge>
    );
  }

  if (clock.lapsed) {
    return (
      <Badge variant="warning" className={className} data-testid={testId} data-clock="lapsed">
        <Clock className="h-3 w-3" aria-hidden />
        {t('holds.clock.lapsed', 'Hold lapsed')}
      </Badge>
    );
  }

  return (
    <Badge
      variant="info"
      className={className}
      data-testid={testId}
      data-clock="counting"
      title={`${t('holds.clock.until', 'until')} ${clock.until}`}
    >
      <Clock className="h-3 w-3" aria-hidden />
      <span className="tabular-nums">
        {t('holds.clock.hold', 'Hold')} · {clock.remaining}
      </span>
      <span className="font-normal opacity-70">
        · {t('holds.clock.until', 'until')} {clock.until}
      </span>
    </Badge>
  );
}

/** Amber, informational — a lapsed soft hold is not an error the operator caused. */
export function HoldLapsedNote({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <p
      className={cn('text-xs text-amber-700 dark:text-amber-300', className)}
      data-testid="hold-lapsed-note"
    >
      {t('holds.lapsedNote', 'Hold lapsed — availability is re-checked at confirm')}
    </p>
  );
}

/**
 * The hold action row. Confirm is ALWAYS present — it is gated only by the
 * group lock (a real server 409), never by the clock. Once the clock lapses
 * Re-hold and Dismiss join as secondary ways out.
 */
export function HoldActions({
  hold,
  lapsed,
  canConfirm = true,
  confirmTitle,
  onConfirm,
  onRelease,
  onRehold,
  onDismiss,
}: {
  hold: PendingHold;
  lapsed: boolean;
  canConfirm?: boolean;
  confirmTitle?: string | undefined;
  onConfirm: () => void;
  onRelease: () => void;
  onRehold: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const busy = hold.busy;

  return (
    <div className="flex flex-wrap justify-end gap-2">
      {lapsed ? (
        <>
          <Button size="sm" variant="ghost" disabled={!!busy} onClick={onDismiss}>
            {t('holds.actions.dismiss', 'Dismiss')}
          </Button>
          <Button size="sm" variant="outline" disabled={!!busy} onClick={onRehold}>
            {busy === 'rehold' ? <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden /> : null}
            {t('holds.actions.rehold', 'Re-hold')}
          </Button>
        </>
      ) : null}
      <Button size="sm" variant="outline" disabled={!!busy} onClick={onRelease}>
        {busy === 'release' ? <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden /> : null}
        {t('holds.actions.release', 'Release')}
      </Button>
      <Button size="sm" disabled={!!busy || !canConfirm} title={confirmTitle} onClick={onConfirm}>
        {busy === 'confirm' ? <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden /> : null}
        {t('holds.actions.confirm', 'Confirm booking')}
      </Button>
    </div>
  );
}

export function HoldRow({
  hold,
  now,
  groupLabel,
  lock,
  onConfirm,
  onRelease,
  onRehold,
  onDismiss,
}: {
  hold: PendingHold;
  now: number;
  groupLabel: string;
  lock: GroupLock;
  onConfirm: () => void;
  onRelease: () => void;
  onRehold: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const clock = holdClock(hold.expiresAt, now);
  // Confirm (= book) is enabled only while the operator holds this group's lock
  // (server 409s lock_not_held otherwise; this keeps the UI honest). The clock
  // never gates it.
  const canConfirm = lock.isMine;
  const lockedBy = lock.holderName ?? t('holds.lock.anotherOperator', 'another operator');

  return (
    <div
      data-testid="hold-row"
      className={cn(
        'rounded-md border p-2.5',
        clock.lapsed
          ? 'border-amber-700/40 bg-amber-50 dark:bg-amber-900/20'
          : 'border-dashed border-border bg-muted/30',
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {t('holds.group', 'Group')} {groupLabel} · {hold.roomCount}{' '}
          {t('holds.rooms', 'room(s)')}
        </span>
        <HoldClockChip clock={clock} />
      </div>
      {/* Chosen outside the policy: the reason the book will record, said on the card */}
      {hold.policyOverride ? (
        <p className="mt-1 text-xs text-amber-800 dark:text-amber-200" data-testid="hold-out-of-policy">
          {t('holds.outOfPolicy', { reason: hold.policyOverride.reason })}
        </p>
      ) : null}
      {clock.lapsed ? <HoldLapsedNote className="mt-1" /> : null}
      {hold.error ? <p className="mt-1 text-xs text-destructive">{hold.error}</p> : null}
      <div className="mt-2">
        <HoldActions
          hold={hold}
          lapsed={clock.lapsed}
          canConfirm={canConfirm}
          confirmTitle={
            canConfirm
              ? undefined
              : `${t('holds.lock.lockedBy', 'Locked by')} ${lockedBy} — ${t('holds.lock.acquireToConfirm', 'acquire the group lock to confirm')}`
          }
          onConfirm={onConfirm}
          onRelease={onRelease}
          onRehold={onRehold}
          onDismiss={onDismiss}
        />
      </div>
      {!canConfirm ? (
        <p className="mt-1 text-right text-[11px] text-amber-700 dark:text-amber-300">
          {t('holds.lock.lockedBy', 'Locked by')} {lockedBy}
          {lock.expiresAt
            ? ` · ${formatRemaining(new Date(lock.expiresAt).getTime() - now)}`
            : ''}
        </p>
      ) : null}
    </div>
  );
}
